import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';

const BITSTAMP_REST_BASE = 'https://www.bitstamp.net';
const BITSTAMP_WS_URL = 'wss://ws.bitstamp.net';
const BITSTAMP_MAX_CANDLES = 1000;
const BITSTAMP_MAX_TRADES = 50;

export const bitstampRestClient = proxyRestOptions.client!;

type BitstampNumber = number | string;

interface BitstampMarket {
  market_symbol: string;
  counter_decimals: number;
  trading: string;
}

interface BitstampTicker {
  last: BitstampNumber;
  high: BitstampNumber;
  low: BitstampNumber;
  volume: BitstampNumber;
  percent_change_24: BitstampNumber;
}

interface BitstampOrderbook {
  timestamp: string;
  bids: BitstampNumber[][];
  asks: BitstampNumber[][];
}

export interface BitstampCandle {
  timestamp: BitstampNumber;
  open: BitstampNumber;
  high: BitstampNumber;
  low: BitstampNumber;
  close: BitstampNumber;
  volume: BitstampNumber;
}

interface BitstampOhlcResponse {
  data?: {
    ohlc?: BitstampCandle[];
  };
}

interface BitstampTransaction {
  date: BitstampNumber;
  price: BitstampNumber;
  amount: BitstampNumber;
  type: BitstampNumber;
}

interface BitstampLiveTrade {
  price: BitstampNumber;
  amount: BitstampNumber;
  timestamp: BitstampNumber;
  type: BitstampNumber;
}

interface BitstampWsEnvelope {
  event?: string;
  channel?: string;
  data?: unknown;
}

const BITSTAMP_CANDLE_STEPS: Partial<Record<IChartTimeframe, number>> = {
  '1m': 60,
  '3m': 180,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '6h': 21600,
  '12h': 43200,
  '24h': 86400,
  '1d': 86400,
  '3d': 259200,
};

const asString = (value: BitstampNumber) => String(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown) => Number.isFinite(Number(value));

const normalizeBookLevels = (levels: BitstampNumber[][] | undefined): string[][] =>
  (levels ?? [])
    .filter(
      level =>
        Array.isArray(level) &&
        level.length >= 2 &&
        isFiniteNumber(level[0]) &&
        isFiniteNumber(level[1]),
    )
    .map(level => [asString(level[0]), asString(level[1])]);

export const buildBitstampMarketSymbol = (base: string, quote: string) =>
  `${base.toLowerCase()}${quote.toLowerCase()}`;

export const normalizeBitstampCandles = (candles: BitstampCandle[]): KLine[] =>
  candles
    .filter(
      candle =>
        isFiniteNumber(candle.timestamp) &&
        isFiniteNumber(candle.open) &&
        isFiniteNumber(candle.high) &&
        isFiniteNumber(candle.low) &&
        isFiniteNumber(candle.close) &&
        isFiniteNumber(candle.volume),
    )
    .map(
      candle =>
        [
          Number(candle.timestamp) * 1000,
          asString(candle.open),
          asString(candle.high),
          asString(candle.low),
          asString(candle.close),
          asString(candle.volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const bitstampPrecisionFromDecimals = (decimals: number): number =>
  Number.isInteger(decimals) && decimals >= 0 ? decimals : 1;

export const buildBitstampSubscriptions = (market: string) => [
  { event: 'bts:subscribe' as const, data: { channel: `order_book_${market}` } },
  { event: 'bts:subscribe' as const, data: { channel: `live_trades_${market}` } },
];

const tickFromPrecision = (precision: number) =>
  precision === 0 ? '1' : `0.${'0'.repeat(Math.max(0, precision - 1))}1`;

export class BitstampStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  asks: string[][] = [];
  bids: string[][] = [];
  liveIntervalMs = 60000;
  currentKline: KLine | undefined;
  lastTicker: ITickerSocket | undefined;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildBitstampMarketSymbol(base, quote);
    this.displayDepth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const asks = [...this.asks]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.displayDepth);
    const bids = [...this.bids]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.displayDepth);
    this.onOrderbookUpdate({ asks, bids });
  };

  applyOrderbook = (book: BitstampOrderbook) => {
    this.asks = normalizeBookLevels(book.asks);
    this.bids = normalizeBookLevels(book.bids);
    this.publishOrderbook();
  };

  publishTicker = (ticker: BitstampTicker) => {
    if (
      !isFiniteNumber(ticker.last) ||
      !isFiniteNumber(ticker.high) ||
      !isFiniteNumber(ticker.low) ||
      !isFiniteNumber(ticker.volume) ||
      !isFiniteNumber(ticker.percent_change_24)
    ) {
      throw new Error(`Bitstamp ticker ${this.pair} has an invalid shape`);
    }

    this.lastTicker = {
      baseVolume: asString(ticker.volume),
      change: normalizeNumber(ticker.percent_change_24),
      close: asString(ticker.last),
      high: asString(ticker.high),
      last: asString(ticker.last),
      low: asString(ticker.low),
      quoteVolume: '',
    };
    this.onTickerUpdate(this.lastTicker);
  };

  publishTradeTicker = (price: string) => {
    this.lastTicker = {
      baseVolume: this.lastTicker?.baseVolume ?? '',
      change: this.lastTicker?.change ?? '',
      close: price,
      high: this.lastTicker?.high ?? '',
      last: price,
      low: this.lastTicker?.low ?? '',
      quoteVolume: this.lastTicker?.quoteVolume ?? '',
    };
    this.onTickerUpdate(this.lastTicker);
  };

  fetchMarket = async () => {
    try {
      const response = await bitstampRestClient.get<BitstampMarket[]>(
        `${BITSTAMP_REST_BASE}/api/v2/markets/`,
      );
      if (!Array.isArray(response.data)) {
        throw new Error('Bitstamp markets response is not an array');
      }
      const market = response.data.find(item => item.market_symbol === this.pair);
      if (!market || market.trading.toLowerCase() !== 'enabled') {
        throw new Error(`Bitstamp market ${this.pair} is unavailable`);
      }

      this.precision = bitstampPrecisionFromDecimals(market.counter_decimals);
      this.selectedPrecision = this.precision;
      this.priceTick = tickFromPrecision(this.precision);
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Bitstamp market error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await bitstampRestClient.get<BitstampOrderbook>(
        `${BITSTAMP_REST_BASE}/api/v2/order_book/${this.pair}/`,
        { params: { group: 1 } },
      );
      if (
        !isRecord(response.data) ||
        !Array.isArray(response.data.asks) ||
        !Array.isArray(response.data.bids)
      ) {
        throw new Error(`Bitstamp orderbook ${this.pair} has an invalid shape`);
      }
      this.applyOrderbook(response.data);
    } catch (error) {
      console.log('Bitstamp orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await bitstampRestClient.get<BitstampTicker>(
        `${BITSTAMP_REST_BASE}/api/v2/ticker/${this.pair}/`,
      );
      if (!isRecord(response.data)) {
        throw new Error(`Bitstamp ticker ${this.pair} has an invalid shape`);
      }
      this.publishTicker(response.data as unknown as BitstampTicker);
    } catch (error) {
      console.log('Bitstamp ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const step = BITSTAMP_CANDLE_STEPS[interval];
    if (!step) return [];

    const boundedLimit = Math.min(BITSTAMP_MAX_CANDLES, Math.max(1, limit));
    const end = to > 0 ? Math.floor((to - 1) / 1000) : Math.floor(Date.now() / 1000);

    try {
      const response = await bitstampRestClient.get<BitstampOhlcResponse>(
        `${BITSTAMP_REST_BASE}/api/v2/ohlc/${this.pair}/`,
        { params: { end, limit: boundedLimit, step } },
      );
      const candles = response.data?.data?.ohlc;
      if (!Array.isArray(candles)) {
        throw new Error(`Bitstamp OHLC ${this.pair} has an invalid shape`);
      }

      return normalizeBitstampCandles(candles)
        .filter(row => (from <= 0 || row[0] >= from) && (to <= 0 || row[0] < to))
        .slice(-boundedLimit);
    } catch (error) {
      console.log('Bitstamp klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({
    limit,
  }: {
    base: string;
    quote: string;
    limit: number;
  }): Promise<TradeSocket[]> => {
    try {
      const response = await bitstampRestClient.get<BitstampTransaction[]>(
        `${BITSTAMP_REST_BASE}/api/v2/transactions/${this.pair}/`,
        { params: { time: 'minute' } },
      );
      if (!Array.isArray(response.data)) {
        throw new Error(`Bitstamp transactions ${this.pair} have an invalid shape`);
      }

      return response.data
        .filter(
          trade =>
            isFiniteNumber(trade.date) &&
            isFiniteNumber(trade.price) &&
            isFiniteNumber(trade.amount),
        )
        .slice(0, Math.min(BITSTAMP_MAX_TRADES, Math.max(1, limit)))
        .map(trade => ({
          price: asString(trade.price),
          quantity: asString(trade.amount),
          timestamp: String(Number(trade.date) * 1000),
          side: Number(trade.type) === 0 ? 'buy' : 'sell',
        }));
    } catch (error) {
      console.log('Bitstamp trades error:', error);
      return [];
    }
  };

  updateKlineFromTrade = (trade: BitstampLiveTrade) => {
    const timestampMs = Number(trade.timestamp) * 1000;
    const price = asString(trade.price);
    const quantity = asString(trade.amount);
    const priceNumber = Number(trade.price);
    const quantityNumber = Number(trade.amount);
    if (
      !Number.isFinite(timestampMs) ||
      !Number.isFinite(priceNumber) ||
      !Number.isFinite(quantityNumber)
    ) {
      return;
    }

    const bucketMs = Math.floor(timestampMs / this.liveIntervalMs) * this.liveIntervalMs;
    if (!this.currentKline || this.currentKline[0] !== bucketMs) {
      this.currentKline = [bucketMs, price, price, price, price, quantity];
    } else {
      this.currentKline = [
        bucketMs,
        this.currentKline[1],
        String(Math.max(Number(this.currentKline[2]), priceNumber)),
        String(Math.min(Number(this.currentKline[3]), priceNumber)),
        price,
        normalizeNumber(Number(this.currentKline[5]) + quantityNumber),
      ];
    }
    this.klineUpdate(this.currentKline);
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.liveIntervalMs = interval > 0 ? interval : 60000;

    const ws = new ReconnectingWebSocket(BITSTAMP_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    ws.onopen = () => {
      this.asks = [];
      this.bids = [];
      this.currentKline = undefined;
      buildBitstampSubscriptions(this.pair).forEach(subscription => {
        ws.send(JSON.stringify(subscription));
      });
    };

    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        const message = JSON.parse(event.data) as BitstampWsEnvelope;

        if (
          message.event === 'data' &&
          message.channel === `order_book_${this.pair}` &&
          isRecord(message.data) &&
          Array.isArray(message.data.asks) &&
          Array.isArray(message.data.bids)
        ) {
          this.applyOrderbook(message.data as unknown as BitstampOrderbook);
          return;
        }

        if (
          message.event === 'trade' &&
          message.channel === `live_trades_${this.pair}` &&
          isRecord(message.data)
        ) {
          const trade = message.data as unknown as BitstampLiveTrade;
          if (
            !isFiniteNumber(trade.price) ||
            !isFiniteNumber(trade.amount) ||
            !isFiniteNumber(trade.timestamp)
          ) {
            return;
          }

          const price = asString(trade.price);
          this.onTradeUpdate({
            price,
            quantity: asString(trade.amount),
            timestamp: String(Number(trade.timestamp) * 1000),
            side: Number(trade.type) === 0 ? 'buy' : 'sell',
          });
          this.publishTradeTicker(price);
          this.updateKlineFromTrade(trade);
          return;
        }

        if (message.event === 'bts:error') {
          console.log('Bitstamp websocket subscription error:', message.data);
        }
      } catch (error) {
        console.log('Bitstamp websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchMarket(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new BitstampStrategy(args);
    await strategy.fetch();
    return strategy;
  };
}
